// Chats del agente compartidos con la empresa —
// ADR-2026-10-13-chats-compartidos-con-la-empresa.
//
// Un chat nace privado (de quien lo abrió). El dueño puede marcarlo "Toda la empresa":
// los compañeros lo ven en el historial y pueden seguir la conversación. Como el hilo
// entero vive en una sola fila (`agent_chats.messages`), dos personas que escriben a la
// vez se pisarían al guardar: por eso el guardado va con versión y, si alguien guardó en
// el medio, se funde lo remoto con lo que escribí yo desde mi última lectura.

export type VisibilidadDeChat = 'privado' | 'empresa';

export const VISIBILIDADES: readonly VisibilidadDeChat[] = ['privado', 'empresa'];

export const ETIQUETAS_VISIBILIDAD: Record<VisibilidadDeChat, { titulo: string; detalle: string }> = {
  privado: { titulo: 'Solo para mí', detalle: 'Nadie más de la empresa lo ve.' },
  empresa: {
    titulo: 'Toda la empresa',
    detalle: 'Tus compañeros lo ven en su historial y pueden seguir la conversación.',
  },
};

/**
 * Lo remoto más lo mío nuevo. Los mensajes no tienen id: "lo mío nuevo" es lo que hay en
 * la lista local después del largo que tenía la última vez que leí o guardé con éxito
 * (`largoSincronizado`). Lo que se pierde en un choque es un cambio sobre un mensaje
 * viejo (una tarjeta que cambió de estado), que es tolerable; un mensaje nuevo, nunca.
 *
 * `activa` es la posición de una respuesta del agente que se guardó A MEDIO ESCRIBIR (el
 * guardado corre cada 600 ms aunque el turno siga). Lo remoto la tiene cortada en esa
 * misma posición, con lo de los compañeros después; acá se reemplaza por la versión
 * completa local SIN moverla, así la conversación queda en orden y no se duplica
 * (review atlas-code#176). Sólo se funde con el turno terminado: mientras se escribe, el
 * texto nuevo va al último mensaje y reordenar lo mandaría al de otro.
 */
export function fusionarMensajes<M>(
  remotos: M[],
  locales: M[],
  largoSincronizado: number,
  activa: number | null = null,
): M[] {
  if (
    activa != null &&
    activa === largoSincronizado &&
    activa < remotos.length &&
    activa < locales.length
  ) {
    return [
      ...remotos.slice(0, activa),
      locales[activa],
      ...remotos.slice(activa + 1),
      ...locales.slice(activa + 1),
    ];
  }
  const desde = Math.max(0, Math.min(largoSincronizado, locales.length));
  return [...remotos, ...locales.slice(desde)];
}

/**
 * Qué queda sincronizado después de un guardado que salió bien. Si el turno sigue y el
 * último mensaje es la respuesta del agente que se está escribiendo, NO cuenta como
 * sincronizada: lo guardado es una versión cortada, y en un choque hay que traer la
 * completa (`activa`). Si no, todo lo guardado está sincronizado.
 */
export function sincroniaTrasGuardar(p: {
  largo: number;
  escribiendo: boolean;
  ultimoEsDelAgente: boolean;
}): { largo: number; activa: number | null } {
  if (p.escribiendo && p.ultimoEsDelAgente && p.largo > 0) {
    return { largo: p.largo - 1, activa: p.largo - 1 };
  }
  return { largo: p.largo, activa: null };
}

/** Lo que se guarda en la pestaña para que una recarga no pierda la versión del chat. */
export interface SincroniaDeChat {
  chatId: string;
  version: string;
  largo: number;
  activa: number | null;
}

/** Lee la sincronía guardada; sólo vale si es del chat que se restaura. */
export function leerSincronia(crudo: string | null, chatId: string | null): SincroniaDeChat | null {
  if (!crudo || !chatId) return null;
  try {
    const s = JSON.parse(crudo) as Partial<SincroniaDeChat>;
    if (s.chatId !== chatId || typeof s.version !== 'string' || typeof s.largo !== 'number') return null;
    return {
      chatId,
      version: s.version,
      largo: Math.max(0, Math.floor(s.largo)),
      activa: typeof s.activa === 'number' ? s.activa : null,
    };
  } catch {
    return null;
  }
}

/**
 * Si el menú ofrece "Visibilidad": sólo con equipo (más de un miembro), con un chat ya
 * guardado (el chat se crea con el primer mensaje) y para su dueño.
 */
export function puedeCambiarVisibilidad(p: {
  varios: boolean;
  chatId: string | null;
  usuarioId: string | null;
  duenoId: string | null | undefined;
}): boolean {
  return p.varios && !!p.chatId && !!p.usuarioId && p.duenoId === p.usuarioId;
}

/** El rótulo "de Juli" del historial: sólo para los chats de otro, cuando se sabe el nombre. */
export function autorParaElHistorial(p: {
  duenoId: string | null;
  usuarioId: string | null;
  nombreDe: (userId: string | null | undefined) => string | null;
}): string | null {
  if (!p.duenoId || p.duenoId === p.usuarioId) return null;
  return p.nombreDe(p.duenoId);
}
